/**
 * MapBottomSheet — Phase F.5 mobile-first bottom sheet for /map.
 *
 * Replaces the stacked side panel on small screens with a sliding,
 * gesture-driven bottom sheet. The desktop side panel is unchanged.
 *
 * UX contract:
 *   1. SNAP POSITIONS:
 *      - "peek"     — handle + headline (about 38% of viewport)
 *      - "expanded" — most of the viewport (~88vh)
 *      - "closed"   — slides off-screen; closing the sheet fires onClose.
 *
 *   2. GESTURES (touch only — desktop falls back to taps on the handle):
 *      - DRAG handle vertically:
 *          • Up past mid-threshold → snaps "expanded"
 *          • Down past mid-threshold → snaps "peek"
 *          • Down past close-threshold from "peek" → snaps "closed"
 *      - SWIPE on the BODY left/right (NOT scrolling) → cycles to the
 *        prev/next ZIP in `coveredZips`. Distinguishes scroll vs swipe
 *        by checking that |dx| > |dy| AND |dx| > MIN_SWIPE_PX. Vertical
 *        gestures on the body still scroll the inner list.
 *
 *   3. DRAG HANDLE — visible 36px-wide pill at top, provides affordance.
 *
 *   4. TAP-TO-SELECT — handled by the parent Map component via the
 *      existing `coverage-fill` Mapbox click handler. When `activeZip`
 *      becomes non-null the sheet auto-snaps to "peek".
 *
 *   5. RENDERING:
 *      - The sheet is `position: fixed; bottom: 0`. Above breakpoint
 *        `min-width: 1024px` the entire component is `display:none` —
 *        desktop continues to use the existing `<ZipSidePanel>` to the
 *        right of the map.
 *
 *   6. ACCESSIBILITY — close button + ARIA labels on the handle and
 *      navigation buttons. Drag is augmented (not replaced) by tap
 *      controls so keyboard / screen-reader users can still navigate.
 *
 * Privacy: same data set as ZipSidePanel — providers come from
 * /api/public/providers-in-zip (already privacy-safe; no addresses).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, ChevronLeft, ChevronRight, X } from "lucide-react";

function fmtPrice(n) {
  if (n == null) return null;
  const v = Number(n);
  return v % 1 === 0 ? `$${v.toFixed(0)}` : `$${v.toFixed(2)}`;
}

function TierBadge({ tier }) {
  if (!tier) return null;
  const label = tier.badge_label || tier.name || "Partner";
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1"
      style={{
        background: "var(--pp-lavender)",
        color: "var(--pp-lavender-text)",
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

// Snap-position constants — `vh` units; converted to px at runtime.
const SNAP_PEEK_VH = 38;
const SNAP_EXPANDED_VH = 12;  // distance from top when expanded
const MIN_SWIPE_PX = 50;      // horizontal-swipe threshold
const VERTICAL_DRAG_CLOSE_PX = 80;  // drag-from-peek down to close

function vhPx(vh) {
  if (typeof window === "undefined") return 0;
  return Math.round((window.innerHeight * vh) / 100);
}

export default function MapBottomSheet({
  activeZip,
  providers,
  loading,
  error,
  onClose,
  coveredZips,
  onSelectZip,
}) {
  // The sheet is always mounted; CSS hides it above the lg breakpoint.
  const [position, setPosition] = useState("closed"); // closed|peek|expanded
  const [drag, setDrag] = useState({
    active: false,
    startY: 0,
    deltaY: 0,
    startX: 0,
    deltaX: 0,
    axis: null,  // "y" once a vertical drag is established
  });
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);

  // Auto-snap to peek when activeZip transitions null→string. Stay
  // closed when activeZip clears.
  useEffect(() => {
    if (activeZip) setPosition("peek");
    else setPosition("closed");
  }, [activeZip]);

  // Body-scroll lock while the sheet is expanded so the underlying
  // map doesn't pan when the user scrolls the provider list.
  useEffect(() => {
    if (position === "expanded" && typeof document !== "undefined") {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [position]);

  // Translate-Y for each snap position (px from top of viewport).
  // CSS `top: 0` + transform: translateY(<n>px).
  const yForPosition = useMemo(() => {
    if (typeof window === "undefined") return 9999;
    const h = window.innerHeight;
    if (position === "closed") return h;
    if (position === "expanded") return vhPx(SNAP_EXPANDED_VH);
    return h - vhPx(SNAP_PEEK_VH);
  }, [position]);

  const onHandleTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    const t = e.touches[0];
    setDrag({
      active: true,
      startY: t.clientY,
      deltaY: 0,
      startX: t.clientX,
      deltaX: 0,
      axis: "y",
    });
  };

  const onHandleTouchMove = (e) => {
    if (!drag.active || !e.touches?.length) return;
    const t = e.touches[0];
    setDrag((d) => ({ ...d, deltaY: t.clientY - d.startY }));
  };

  const onHandleTouchEnd = () => {
    if (!drag.active) return;
    const dy = drag.deltaY;
    if (position === "expanded") {
      // From expanded: small drag down → peek, large drag down → close.
      if (dy > 200) setPosition("closed");
      else if (dy > 60) setPosition("peek");
      else if (dy < -40) setPosition("expanded"); // no-op
      else setPosition("expanded");
    } else if (position === "peek") {
      if (dy < -60) setPosition("expanded");
      else if (dy > VERTICAL_DRAG_CLOSE_PX) {
        setPosition("closed");
      } else setPosition("peek");
    }
    setDrag({
      active: false,
      startY: 0, deltaY: 0,
      startX: 0, deltaX: 0,
      axis: null,
    });
  };

  // Settle the close action by firing onClose AFTER the slide-down
  // animation completes. Avoids a flash of "closed" before the parent
  // also clears activeZip.
  useEffect(() => {
    if (position === "closed" && activeZip) {
      const id = setTimeout(() => onClose?.(), 220);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [position, activeZip, onClose]);

  // Horizontal swipe between ZIPs — listens on the body, but only
  // commits when the gesture is dominantly horizontal and crosses the
  // pixel threshold. Vertical gestures fall through to native scroll.
  const onBodyTouchStart = (e) => {
    if (!e.touches?.length) return;
    const t = e.touches[0];
    bodyTouchRef.current = {
      startX: t.clientX,
      startY: t.clientY,
      committed: false,
    };
  };
  const bodyTouchRef = useRef({ startX: 0, startY: 0, committed: false });
  const onBodyTouchEnd = (e) => {
    const ref = bodyTouchRef.current;
    if (!ref || ref.committed) return;
    const t = (e.changedTouches && e.changedTouches[0]);
    if (!t) return;
    const dx = t.clientX - ref.startX;
    const dy = t.clientY - ref.startY;
    if (Math.abs(dx) > MIN_SWIPE_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
      ref.committed = true;
      const idx = (coveredZips || []).indexOf(activeZip);
      if (idx < 0) return;
      if (dx < 0) {
        // swipe left → next zip
        const next = coveredZips[(idx + 1) % coveredZips.length];
        onSelectZip?.(next);
      } else {
        // swipe right → previous zip
        const prev = coveredZips[(idx - 1 + coveredZips.length) % coveredZips.length];
        onSelectZip?.(prev);
      }
    }
  };

  const prevZip = () => {
    const list = coveredZips || [];
    if (!list.length) return;
    const idx = list.indexOf(activeZip);
    if (idx < 0) return;
    onSelectZip?.(list[(idx - 1 + list.length) % list.length]);
  };
  const nextZip = () => {
    const list = coveredZips || [];
    if (!list.length) return;
    const idx = list.indexOf(activeZip);
    if (idx < 0) return;
    onSelectZip?.(list[(idx + 1) % list.length]);
  };

  // While dragging, apply the live deltaY so the sheet follows the
  // finger; otherwise sit at the snap position with a smooth tween.
  const currentY = drag.active ? yForPosition + drag.deltaY : yForPosition;

  return (
    <div
      ref={sheetRef}
      role="dialog"
      aria-label="ZIP details"
      aria-hidden={position === "closed"}
      data-testid="map-bottom-sheet"
      className="lg:hidden"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        pointerEvents: position === "closed" ? "none" : "auto",
        zIndex: 50,
      }}
    >
      {/* Scrim — fades in when sheet is open; tap to close. Only
          visible when expanded so the map remains glanceable at peek. */}
      <div
        onClick={() => setPosition("closed")}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(20, 22, 28, 0.45)",
          opacity: position === "expanded" ? 1 : 0,
          transition: "opacity 220ms ease",
          pointerEvents: position === "expanded" ? "auto" : "none",
        }}
        data-testid="map-bottom-sheet-scrim"
        aria-hidden
      />

      {/* Sheet body */}
      <div
        style={{
          position: "absolute",
          left: 0, right: 0,
          top: 0,
          height: "100vh",
          transform: `translateY(${Math.max(0, currentY)}px)`,
          transition: drag.active
            ? "none"
            : "transform 240ms cubic-bezier(0.22, 1, 0.36, 1)",
          background: "var(--pp-v3-surface)",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          boxShadow: "0 -10px 40px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Drag handle */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Drag to expand or collapse"
          onTouchStart={onHandleTouchStart}
          onTouchMove={onHandleTouchMove}
          onTouchEnd={onHandleTouchEnd}
          onClick={() =>
            setPosition(position === "expanded" ? "peek" : "expanded")
          }
          data-testid="map-bottom-sheet-handle"
          style={{
            padding: "10px 0 6px",
            cursor: "grab",
            touchAction: "none",
            flex: "0 0 auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 44,
              height: 5,
              borderRadius: 99,
              background: "var(--pp-line)",
            }}
          />
        </div>

        {/* Header — ZIP label + close + L/R nav */}
        <div
          className="px-5"
          style={{ flex: "0 0 auto" }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={prevZip}
                aria-label="Previous ZIP"
                data-testid="map-bottom-sheet-prev"
                className="rounded-full w-8 h-8 inline-flex items-center justify-center"
                style={{
                  color: "var(--pp-text-soft)",
                  border: "1px solid var(--pp-line)",
                  background: "var(--pp-v3-bg)",
                }}
              >
                <ChevronLeft size={16} />
              </button>
              <div>
                <div
                  className="pp-kick"
                  style={{ fontSize: 11, color: "var(--pp-text-soft)" }}
                >
                  ZIP {activeZip || "—"}
                </div>
                <h3
                  className="pp-h2"
                  style={{
                    fontSize: 18,
                    letterSpacing: "-0.01em",
                    margin: 0,
                  }}
                  data-testid="map-bottom-sheet-heading"
                >
                  Plucking this area
                </h3>
              </div>
              <button
                type="button"
                onClick={nextZip}
                aria-label="Next ZIP"
                data-testid="map-bottom-sheet-next"
                className="rounded-full w-8 h-8 inline-flex items-center justify-center"
                style={{
                  color: "var(--pp-text-soft)",
                  border: "1px solid var(--pp-line)",
                  background: "var(--pp-v3-bg)",
                }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setPosition("closed")}
              aria-label="Close ZIP details"
              data-testid="map-bottom-sheet-close"
              className="rounded-full w-9 h-9 inline-flex items-center justify-center"
              style={{
                color: "var(--pp-text-soft)",
                border: "1px solid var(--pp-line)",
                background: "var(--pp-v3-bg)",
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Scrollable body — gesture handlers detect horizontal swipes
            and ignore (let through) vertical scrolls. */}
        <div
          ref={bodyRef}
          onTouchStart={onBodyTouchStart}
          onTouchEnd={onBodyTouchEnd}
          data-testid="map-bottom-sheet-body"
          className="px-5 pt-4 pb-8"
          style={{
            flex: "1 1 auto",
            overflowY: position === "expanded" ? "auto" : "hidden",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {loading && (
            <p
              className="pp-sm"
              style={{ color: "var(--pp-text-soft)" }}
              data-testid="map-bottom-sheet-loading"
            >
              Loading PooPartners…
            </p>
          )}
          {error && (
            <p
              className="pp-sm"
              style={{ color: "#B91C1C" }}
              data-testid="map-bottom-sheet-error"
            >
              {error}
            </p>
          )}
          {!loading && !error && (providers || []).length === 0 && (
            <p
              className="pp-sm"
              style={{ color: "var(--pp-text-soft)" }}
              data-testid="map-bottom-sheet-empty-providers"
            >
              No PooPartners covering this ZIP yet. Swipe to a neighboring ZIP
              or try the metro waitlist below.
            </p>
          )}
          {!loading && !error && (providers || []).length > 0 && (
            <ul
              className="space-y-3"
              data-testid="map-bottom-sheet-provider-list"
            >
              {(providers || []).map((p) => {
                const price = fmtPrice(p.per_dog_price);
                return (
                  <li
                    key={p.id}
                    className="rounded-[16px] p-4"
                    style={{
                      background: "var(--pp-v3-bg)",
                      border: "1px solid var(--pp-line)",
                    }}
                    data-testid={`map-bottom-sheet-provider-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="font-semibold" style={{ fontSize: 15 }}>
                        {p.business_name}
                      </div>
                      {price && (
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 14,
                            color: "var(--pp-slate)",
                          }}
                        >
                          {price}
                          <span
                            style={{
                              color: "var(--pp-text-soft)",
                              fontWeight: 500,
                              fontSize: 12,
                            }}
                          >
                            {" / dog"}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <TierBadge tier={p.tier} />
                    </div>
                    <Link
                      to={`/book?provider_id=${encodeURIComponent(p.id)}`}
                      className="pp-btn pp-btn-ghost w-full justify-center"
                      data-testid={`map-bottom-sheet-book-${p.id}`}
                    >
                      Book with this partner
                      <ArrowRight size={14} aria-hidden />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          {/* Peek-state hint */}
          {position === "peek" && (
            <p
              className="pp-sm mt-3"
              style={{ color: "var(--pp-text-soft)", fontSize: 12 }}
              data-testid="map-bottom-sheet-peek-hint"
            >
              <MapPin
                size={12}
                style={{
                  display: "inline-block",
                  verticalAlign: "middle",
                  marginRight: 4,
                }}
              />
              Pull up to see partners · swipe ↔ to change ZIP · pull down to close
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
